import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import Dashboard from "@/pages/Dashboard";
import Settings from "@/pages/Settings";
import Login from "@/pages/Login";
import UserManagement from "@/pages/UserManagement";
import axios from "axios";
import { useStableConnection, ConnectionStatus } from "@/hooks/useStableConnection";
import ConnectionIndicator from "@/components/ConnectionIndicator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AppContext = {
  API,
  BACKEND_URL
};

function App() {
  const [cameras, setCameras] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Check for existing session
  useEffect(() => {
    const initAuth = async () => {
      // Initialize admin user
      try {
        await axios.post(`${API}/auth/init-admin`);
      } catch (e) {}
      
      const token = localStorage.getItem("token");
      const savedUser = localStorage.getItem("user");
      
      if (token && savedUser) {
        try {
          // Verify token is still valid
          await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(JSON.parse(savedUser));
        } catch (error) {
          // Token expired or invalid
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        }
      }
      setAuthChecked(true);
    };
    
    initAuth();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      // Initialize default data first
      await axios.post(`${API}/init-defaults`);
      
      // Fetch cameras and sensors
      const [camerasRes, sensorsRes] = await Promise.all([
        axios.get(`${API}/cameras`),
        axios.get(`${API}/sensors`)
      ]);
      
      setCameras(camerasRes.data);
      setSensors(sensorsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error; // Propagar error para el sistema de estabilidad
    } finally {
      setLoading(false);
    }
  }, []);

  // Sistema de estabilidad - reconexión automática
  const {
    connectionStatus,
    lastSuccessfulFetch,
    retryCount,
    forceReconnect,
    isConnected
  } = useStableConnection(fetchData, !!user);

  // Notificar cuando se pierde/recupera conexión
  useEffect(() => {
    if (connectionStatus === ConnectionStatus.DISCONNECTED) {
      toast.error('Conexión perdida - Reintentando...', { duration: 5000 });
    } else if (connectionStatus === ConnectionStatus.ERROR) {
      toast.error('Error de conexión - Click en "Reintentar"', { duration: 10000 });
    }
  }, [connectionStatus]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [fetchData, user]);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  const updateCamera = async (cameraId, data) => {
    try {
      const response = await axios.put(`${API}/cameras/${cameraId}`, data);
      setCameras(prev => prev.map(cam => 
        cam.id === cameraId ? response.data : cam
      ));
      return response.data;
    } catch (error) {
      console.error("Error updating camera:", error);
      throw error;
    }
  };

  const updateSensor = async (sensorId, data) => {
    try {
      const response = await axios.put(`${API}/sensors/${sensorId}`, data);
      setSensors(prev => prev.map(sensor => 
        sensor.id === sensorId ? response.data : sensor
      ));
      return response.data;
    } catch (error) {
      console.error("Error updating sensor:", error);
      throw error;
    }
  };

  // Show loading while checking auth
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-zinc-500">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route 
            path="/login" 
            element={
              user ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
            } 
          />
          <Route 
            path="/" 
            element={
              user ? (
                <Dashboard 
                  cameras={cameras} 
                  sensors={sensors} 
                  loading={loading}
                  onRefresh={fetchData}
                  user={user}
                  onLogout={handleLogout}
                  connectionStatus={connectionStatus}
                  lastSuccessfulFetch={lastSuccessfulFetch}
                  retryCount={retryCount}
                  onReconnect={forceReconnect}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/settings" 
            element={
              user ? (
                <Settings 
                  cameras={cameras}
                  sensors={sensors}
                  onUpdateCamera={updateCamera}
                  onUpdateSensor={updateSensor}
                  onRefresh={fetchData}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/users" 
            element={
              user?.role === "admin" ? (
                <UserManagement currentUser={user} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-left" richColors toastOptions={{ className: "z-[100]" }} />
      <div className="noise-overlay" />
    </div>
  );
}

export default App;
